import { Col, Form, Row } from "react-bootstrap";
import {
    AutoCol,
    Checkbox,
    DateInput,
    FormLabelText,
    Input,
    RadioGroup,
    RadioOption,
    Textarea,
} from "../../../component-library";
import { QueryType, YesNoRadioOptions } from "../../../models";
import { RenderedFormProps } from "../RenderForm";
import { useTissueQuery } from "../../../queries";
import { displayDate, getValueOrBlank } from "../../../helper-functions";

export function PostOp3To6Months({ formState }: Readonly<RenderedFormProps>) {
    const {
        detailQuery: { data: tissue },
    } = useTissueQuery({ queryType: QueryType.Detail, id: formState.formData?.tissueId });
    const getName = (firstName: string | undefined, lastName: string | undefined) => {
        return `${getValueOrBlank(firstName)}${" "} ${getValueOrBlank(lastName)}`;
    };

    const culturesPerformedOnOptions: RadioOption[] = [
        { label: "Media Only", value: "mediaOnly" },
        { label: "Corneo-scleral rim", value: "corneoScleralRim" },
        { label: "Both", value: "both" },
    ];
    const commentsOptions: RadioOption[] = [
        { label: "None noted", value: "none" },
        { label: "Yes, see comments", value: "yes" },
    ];

    return (
        <>
            <Row style={{ textAlign: "center", marginTop: "10px", marginBottom: "10px" }}>
                <Col xxl={12}>
                    <h1>3-6 MONTH POST-OPERATIVE FOLLOW-UP REPORT</h1>
                </Col>
                <Col xxl={12}>
                    Advancing Sight Network is <strong>Required</strong> by the Eye Bank Association of America to
                    obtain the following information
                </Col>
                <Col>
                    <h1>PLEASE PROVIDE ANY MISSING INFORMATION (FAX TO: (888)321-8539)</h1>
                </Col>
            </Row>

            <h2>Recipient Information</h2>
            <Row>
                <Col xxl={3}>
                    <FormLabelText
                        label="Name"
                        text1={getName(tissue?.surgery?.recipientFirstName, tissue?.surgery?.recipientLastName)}
                    />
                </Col>
                <Col xxl={2}>
                    <FormLabelText
                        label="Date of Birth"
                        text1={getValueOrBlank(displayDate(tissue?.surgery?.recipientBirthDate))}
                    />
                </Col>
                <Col xxl={1}>
                    <FormLabelText label="Age" text1={getValueOrBlank(tissue?.surgery?.recipientAge)} />
                </Col>
                <AutoCol>
                    <FormLabelText
                        label="Unique ID Number (SSN, Driver's License, Hospital ID, Medical Record)"
                        text1={getValueOrBlank(tissue?.surgery?.recipientUid)}
                    />
                </AutoCol>
            </Row>
            <h2 style={{ marginTop: "20px" }}>Ocular Diagnosis</h2>
            <Row>
                <Col xxl={6}>
                    <Row>
                        <Col xxl={12} className="mb-2">
                            Ocular Diagnosis: Operative Eye
                        </Col>
                        <AutoCol>
                            <Checkbox accessor="pbkOperative" label="Pseudophakic Bullous Keratopathy" inline />
                        </AutoCol>
                        <AutoCol>
                            <Checkbox accessor="corneaEdemaOperative" label="Cornea Edema" inline />
                        </AutoCol>
                        <AutoCol>
                            <Checkbox accessor="cornealUlcerOperative" label="Corneal Ulcer" inline />
                        </AutoCol>
                        <AutoCol>
                            <Checkbox accessor="stromalScarringOperative" label="Stromal Scarring" inline />
                        </AutoCol>
                        <AutoCol>
                            <Checkbox accessor="traumaOperative" label="Trauma" inline />
                        </AutoCol>
                        <AutoCol>
                            <Checkbox accessor="keratoconusOperative" label="Keratoconus" inline />
                        </AutoCol>
                        <AutoCol>
                            <Checkbox accessor="fuchsDystrophyOperative" label="Fuchs Dystrophy" inline />
                        </AutoCol>
                        <AutoCol>
                            <Checkbox accessor="graftFailureOperative" label="Graft Failure" inline />
                        </AutoCol>
                        <Col xxl={12}>
                            <Input accessor="otherOperative" label="Other:" inlineLabel />
                        </Col>
                    </Row>
                </Col>
                <Col xxl={6}>
                    <Row>
                        <Col xxl={12} className="mb-2">
                            Ocular Diagnosis: Non-operative Eye
                        </Col>
                        <AutoCol>
                            <Checkbox accessor="pbkNonOperative" label="Pseudophakic Bullous Keratopathy" inline />
                        </AutoCol>
                        <AutoCol>
                            <Checkbox accessor="corneaEdemaNonOperative" label="Cornea Edema" inline />
                        </AutoCol>
                        <AutoCol>
                            <Checkbox accessor="cornealUlcerNonOperative" label="Corneal Ulcer" inline />
                        </AutoCol>
                        <AutoCol>
                            <Checkbox accessor="stromalScarringNonOperative" label="Stromal Scarring" inline />
                        </AutoCol>
                        <AutoCol>
                            <Checkbox accessor="traumaNonOperative" label="Trauma" inline />
                        </AutoCol>
                        <AutoCol>
                            <Checkbox accessor="keratoconusNonOperative" label="Keratoconus" inline />
                        </AutoCol>
                        <AutoCol>
                            <Checkbox accessor="fuchsDystrophyNonOperative" label="Fuchs Dystrophy" inline />
                        </AutoCol>
                        <AutoCol>
                            <Checkbox accessor="graftFailureNonOperative" label="Graft Failure" inline />
                        </AutoCol>
                        <Col xxl={12}>
                            <Input accessor="otherNonOperative" label="Other:" inlineLabel />
                        </Col>
                    </Row>
                </Col>
            </Row>
            <Row className="mt-2">
                <Col xxl={12} style={{ display: "flex" }}>
                    <span>
                        <RadioGroup
                            accessor="complications"
                            groupName=""
                            options={YesNoRadioOptions}
                            inline
                            inlineGroupName
                        />
                    </span>
                    <span>
                        <Form.Label>
                            <strong>Were there any complications during surgery, </strong>
                            <em>If yes describe in the comment section</em>
                        </Form.Label>
                    </span>
                </Col>
            </Row>
            <Row>
                <Col xxl={6}>
                    <FormLabelText label="Tissue ID Number" text1={getValueOrBlank(tissue?.number)} />
                </Col>
                <Col xxl={6}>
                    <FormLabelText label="P.O. Number" text1={getValueOrBlank(tissue?.surgery?.poNumber)} />
                </Col>
            </Row>
            <div style={{ marginTop: "10px" }}>
                <Row>
                    <Col xxl={6}>
                        <FormLabelText
                            label="Surgeon Name"
                            text1={getName(tissue?.surgery?.surgeon?.firstName, tissue?.surgery?.surgeon?.lastName)}
                        />
                    </Col>
                    <Col xxl={6}>
                        <FormLabelText
                            label="Surgery Facility"
                            text1={getValueOrBlank(tissue?.surgery?.locationAccount?.name)}
                        />
                    </Col>
                </Row>
            </div>
            <div style={{ marginTop: "10px" }}>
                <Row>
                    <Col xxl={6}>
                        <FormLabelText
                            label="Date of Surgery"
                            text1={getValueOrBlank(displayDate(tissue?.surgery?.surgeryDate))}
                        />
                    </Col>
                    <Col xxl={6}>
                        <FormLabelText
                            label="Address"
                            text1={getValueOrBlank(tissue?.surgery?.locationAccount?.address1)}
                        />
                    </Col>
                </Row>
            </div>
            <div style={{ marginTop: "10px" }}>
                <Row>
                    <Col xxl={6}>
                        <FormLabelText
                            label="Type of Surgery"
                            text1={getValueOrBlank(tissue?.surgery?.surgeryType?.name)}
                        />
                        <span style={{ marginLeft: "20px" }}>
                            <Form.Label>Other: </Form.Label>
                        </span>
                        <span>
                            <Form.Text> {getValueOrBlank(tissue?.surgery?.otherProcedure)}</Form.Text>
                        </span>
                    </Col>
                    <Col xxl={2}>
                        <FormLabelText label="City" text1={getValueOrBlank(tissue?.surgery?.locationAccount?.city)} />
                    </Col>
                    <Col xxl={2}>
                        <FormLabelText label="State" text1={getValueOrBlank(tissue?.surgery?.locationAccount?.state)} />
                    </Col>
                    <Col xxl={2}>
                        <FormLabelText label="Zip" text1={getValueOrBlank(tissue?.surgery?.locationAccount?.zip)} />
                    </Col>
                </Row>
            </div>

            <h3 style={{ marginTop: "10px" }}>
                {" "}
                Advancing Sight Network is <strong>Required</strong> by the Eye Bank Association of America to request
                the following information in regard to adverse reactions
            </h3>

            <div style={{ marginTop: "10px" }}>
                <div>
                    <Form.Label>
                        <em>
                            Did the recipient experience an{" "}
                            <strong>infection manifested by Keratitis, Endophthalmitis, or systemic disease</strong>{" "}
                            following the transplant:
                        </em>{" "}
                    </Form.Label>
                    <RadioGroup
                        accessor="experiencedInfection"
                        groupName=""
                        options={YesNoRadioOptions}
                        inline
                        inlineGroupName
                    />
                </div>
                <div style={{ display: "flex", marginLeft: "30px" }}>
                    <span>
                        <strong>If yes, </strong>what date was infection diagnosed:
                    </span>
                    <span style={{ marginLeft: "5px" }}>
                        <DateInput accessor="infectionDiagnosedDate" label="" inlineLabel disabled={formState.isReadonly} />
                    </span>
                </div>
                <div style={{ display: "flex", marginLeft: "30px" }}>
                    <span>
                        <strong>If yes, </strong>was donor tissue the suspected source:{" "}
                    </span>
                    <span style={{ marginLeft: "5px" }}>
                        <RadioGroup
                            accessor="wasDonorTissueSource"
                            groupName=""
                            options={YesNoRadioOptions}
                            inline
                            inlineGroupName
                        />
                    </span>
                </div>
                <div style={{ display: "flex", marginLeft: "30px", alignItems: "center" }}>
                    <span>
                        <strong>If yes, </strong>please describe the infection:{" "}
                    </span>
                    <span style={{ marginLeft: "5px", flexGrow: 1 }}>
                        <Textarea accessor="infectionExplanation" label="" rows={2} />
                    </span>
                </div>
            </div>
            <div style={{ marginTop: "10px" }}>
                <div>
                    <Form.Label>
                        <em>
                            Did the recipient experience a{" "}
                            <strong>
                                biologic dysfunction (such as immediate endothelial failure, donor corneal dystrophy,
                                malignancy, or evidence suggestive of prior refractive surgery){" "}
                            </strong>
                            following the transplant:{" "}
                        </em>
                    </Form.Label>
                    <RadioGroup
                        accessor="experiencedDysfunction"
                        groupName=""
                        options={YesNoRadioOptions}
                        inline
                        inlineGroupName
                    />
                </div>
                <div style={{ display: "flex", marginLeft: "30px" }}>
                    <span>
                        <strong>If yes, </strong>which one:
                    </span>
                    <span style={{ marginLeft: "5px" }}>
                        <Input accessor="dysfunction" label="" />
                    </span>
                </div>
                <div style={{ display: "flex", marginLeft: "30px" }}>
                    <span>
                        <strong>If yes, </strong>what date was the biological dysfunction diagnosed:{" "}
                    </span>
                    <span style={{ marginLeft: "5px" }}>
                        <DateInput accessor="dysfunctionDiagnosedDate" label="" disabled={formState.isReadonly} />
                    </span>
                </div>
                <div style={{ display: "flex", marginLeft: "30px" }}>
                    <span>
                        <strong>If yes, </strong>was donor tissue the suspected source:{" "}
                    </span>
                    <span style={{ marginLeft: "5px" }}>
                        <RadioGroup
                            accessor="wasDonorTissueSourceDysfunction"
                            groupName=""
                            options={YesNoRadioOptions}
                            inline
                            inlineGroupName
                        />
                    </span>
                </div>
            </div>
            <div style={{ marginTop: "10px" }}>
                <div style={{ display: "flex" }}>
                    <span>
                        {" "}
                        <Form.Label>
                            <em>
                                Was <strong>Donor culturing </strong>
                                performed:{" "}
                            </em>
                        </Form.Label>
                        <RadioGroup
                            accessor="donorCulturingPerformed"
                            groupName=""
                            options={YesNoRadioOptions}
                            inline
                            inlineGroupName
                        />
                    </span>
                    <span style={{ marginLeft: "5px" }}>
                        <em>
                            <strong> (applies to donor tissue only)</strong>
                        </em>
                    </span>
                </div>
                <div style={{ display: "flex", marginLeft: "30px" }}>
                    <span>
                        <strong>If yes, </strong>what were the cultures performed on:
                    </span>
                    <span style={{ marginLeft: "5px" }}>
                        <RadioGroup
                            accessor="culturesPerformedOn"
                            groupName=""
                            options={culturesPerformedOnOptions}
                            inline
                            inlineGroupName
                        />
                    </span>
                </div>
                <div style={{ display: "flex", marginLeft: "30px" }}>
                    <span>
                        <strong>If yes, </strong>was the culture positive:{" "}
                    </span>
                    <span style={{ marginLeft: "5px" }}>
                        <RadioGroup
                            accessor="positiveCulture"
                            groupName=""
                            options={YesNoRadioOptions}
                            inline
                            inlineGroupName
                        />
                    </span>
                </div>
                <div style={{ display: "flex", marginLeft: "30px", alignItems: "center" }}>
                    <span>
                        <strong>If yes, </strong>organism(s) identified:{" "}
                    </span>
                    <span style={{ marginLeft: "5px", flexGrow: 1 }}>
                        <Input accessor="organismIdentified" label="" />
                    </span>
                </div>
            </div>
            <div style={{ marginTop: "10px" }}>
                <Form.Label>
                    <em>Any additional post-operative findings or comments: </em>
                </Form.Label>
                <RadioGroup accessor="anyComments" groupName="" options={commentsOptions} inline inlineGroupName />
                <Textarea rows={2} accessor="comments" label="Comments:" inlineLabel />
            </div>
        </>
    );
}
